import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InvoiceInfo from "features/invoices/components/InvoiceInfo";
import RoundButton from "features/shared/components/buttons/RoundButton";
import DateFormatter from "features/shared/components/formatters/DateFormatter";
import PriceFormatter from "features/shared/components/formatters/PriceFormatter";
import DocumentDownloadButton from "features/shared/components/documents/DocumentDownloadButton";
import PaymentUsageDTO from "features/payments/dtos/PaymentUsageDTO";
import styles from "features/payments/components/paymentusagedetail.module.scss";
import { getInvoiceDetailRoute } from "features/shared/helpers/routeHelper";

interface Props {
  paymentUsage: PaymentUsageDTO;
  showInvoiceTimePeriod?: boolean;
}

const DepositUsageDetail: React.FC<Props> = ({ paymentUsage, showInvoiceTimePeriod }) => {
  const invoice = paymentUsage.invoiceUsage ?? null;
  const payDoc = paymentUsage.paymentDocument ?? null;

  const amountCurrency = invoice?.currency ?? payDoc?.currency;

  const detailInvoiceType = invoice?.invoiceType ?? payDoc?.invoiceType!;
  const detailInvoiceId = invoice?.invoiceId ?? payDoc?.invoiceId!;
  const proformaId = invoice?.proformaInvoiceId;

  return (
    <div className="row align-items-center no-gutters">
      <div className="col">
        <div className="row no-gutters align-items-center">
          <div className="col-sm-7 col-lg-8">
            <div className="row no-gutters align-items-center">
              <div className="col-sm-7 col-lg-5">
                {invoice ? (
                  <InvoiceInfo
                    invoiceType={invoice.invoiceType!}
                    invoiceSubType={invoice.invoiceSubType!}
                    commodityDetails={invoice.commodityDetail ?? []}
                    entityLinks={invoice.entityLinks ?? []}
                    timePeriodFrom={invoice.timePeriodFrom}
                    timePeriodTo={invoice.timePeriodTo}
                    mobileBehaviour
                    showTimePeriod={showInvoiceTimePeriod}
                  />
                ) : payDoc ? (
                  <InvoiceInfo
                    invoiceType={payDoc.invoiceType!}
                    invoiceSubType={payDoc.invoiceSubType!}
                    commodityDetails={payDoc.commodityDetail ?? []}
                    entityLinks={payDoc.entityLinks ?? []}
                    timePeriodFrom={payDoc.timePeriodFrom}
                    timePeriodTo={payDoc.timePeriodTo}
                    mobileBehaviour
                    showTimePeriod={showInvoiceTimePeriod}
                  />
                ) : (
                  <span className="text-muted">–</span>
                )}
              </div>

              <div className="col-sm-5 col-lg-7">
                <div className="row no-gutters align-items-lg-center">
                  <div className="col-auto col-sm-12 col-lg-6 text-muted text-truncate pr-3">
                    <span className="d-sm-none mr-2 text-muted">
                      <FontAwesomeIcon icon="file-invoice" />
                    </span>
                    <span title={(invoice?.invoiceNumber ?? "") || "–"}>
                      {invoice?.invoiceNumber ?? "–"}
                    </span>
                  </div>

                  <div className={`col-auto col-sm-12 col-lg-6 ${styles.dueDateItem}`}>
                    <span className="d-sm-none mr-2 text-muted">
                      <FontAwesomeIcon icon="clock" />
                    </span>
                    {invoice?.dueDate ? <DateFormatter date={invoice.dueDate} /> : <span>–</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-5 col-lg-4">
            <div className="row no-gutters align-items-center">
              <div className="col-auto col-sm-6 order-1 order-sm-0">
                <span className="d-sm-none mr-2 text-muted">
                  <FontAwesomeIcon icon="donate" />
                </span>
                <PriceFormatter amount={paymentUsage.amount ?? 0} currency={amountCurrency!} />
              </div>

              <div className="col-auto col-sm-6 text-truncate text-muted pr-3 pr-sm-0 d-flex align-items-center">
                <span className="d-sm-none mr-2 text-muted">
                  <FontAwesomeIcon icon="file-invoice" />
                </span>
                <span className="text-truncate" title={payDoc?.invoiceNumber ?? "–"}>
                  {payDoc?.invoiceNumber ?? "–"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`col-auto pl-3 ${styles.btnCol}`}>
        <div className="d-flex flex-column flex-lg-row align-items-end align-items-lg-center justify-content-lg-end">
          {payDoc?.documentSimplified && payDoc.documentSimplified.length > 0 && (
            <div className="mb-2 mb-lg-0 mr-lg-2">
              <DocumentDownloadButton
                id={payDoc.invoiceId!}
                icon="file-pdf"
                documents={payDoc.documentSimplified}
              />
            </div>
          )}
          <RoundButton asLink to={getInvoiceDetailRoute(detailInvoiceType, detailInvoiceId, proformaId)} />
        </div>
      </div>
    </div>
  );
};

export default DepositUsageDetail;
